import { NonNullableFormBuilder, Validators } from '@angular/forms';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { noop } from 'rxjs';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  BASE_PERMISSIONS,
  BaseButtonSize,
  BaseCoreState,
  BaseCustomerInquiryOfferInterface,
  BaseFilterService,
  baseInquiryActions,
  BaseInquiryShifts,
  BaseInquiryShiftsWithFlex,
  BaseModalService,
  BaseOfferStatus,
  BaseRolesService,
  CustomerInquiryOfferListItemResponse,
} from 'base';
import { BaseCustomerOfferRejectionCauses } from '../../_const/base-customer-offer-rejection-causes.const';
import { BaseCustomerOfferPositionsToAcceptInterface } from '../../_interfaces/base-customer-offer-positions-to-accept.interface';
import { BaseCustomerOfferActions } from '../../_enums/base-customer-offer-actions.enum';
import { BaseCustomerLocationWorkCouncilSettingsInterface } from '../../../../shared/_interfaces/base-customer-location-work-council-settings.interface';
import { BaseCustomerOfferActionsService } from '../../../../shared/_services/base-customer-offer-actions.service';

@Component({
  selector: 'base-customer-unlimited-offer-actions',
  templateUrl: './base-customer-unlimited-offer-actions.component.html',
})
export class BaseCustomerUnlimitedOfferActionsComponent implements OnInit, OnChanges {
  @ViewChild('acceptOfferModal') acceptOfferModal: ElementRef;
  @ViewChild('rejectOfferTemplate') rejectOfferTemplate: ElementRef;
  @Input() offer: BaseCustomerInquiryOfferInterface;
  @Input() offerAction: BaseCustomerOfferActions;
  @Input() locationWorkCouncilSettings: BaseCustomerLocationWorkCouncilSettingsInterface;
  @Output() openOfferInfoModal = new EventEmitter<CustomerInquiryOfferListItemResponse & { order?: number }>();

  modalRef: NgbModalRef;
  isOfferConfirmed: boolean;
  isOfferOpen: boolean;
  isOfferDeclined: boolean;
  isOfferConfirmationNeeded = false;
  isOfferUpdateNeeded = false;
  isAcceptOfferAvailable: boolean;
  isShiftsDeselectionMode: boolean;
  isAcceptButtonAvailable = false;
  isRejectButtonAvailable = false;
  isWithdrawButtonAvailable = false;
  isPreselectButtonAvailable = false;
  isUnselectButtonAvailable = false;
  profileName: string;
  rejectOfferForm = this.fb.group({
    decliningCategory: ['', Validators.required],
    decliningNote: [''],
  });
  isRejectFormSubmitted = false;
  offerPositionsToAccept: BaseCustomerOfferPositionsToAcceptInterface[];
  rejectionCauseList = BaseCustomerOfferRejectionCauses;
  buttonSizes = BaseButtonSize;
  canAcceptOffers: boolean;

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private fb: NonNullableFormBuilder,
    private translatePipe: TranslatePipe,
    private rolesService: BaseRolesService,
    public filterService: BaseFilterService,
    private offerActionsService: BaseCustomerOfferActionsService
  ) {}

  ngOnInit() {
    this.setOfferCreationAvailability();
    this.setStatusValues();
    this.initializeActionButtonStates();

    this.canAcceptOffers = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_ACCEPT_OFFER);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.offer.data.status === BaseOfferStatus.open && changes?.offer && !changes.offer.firstChange) {
      this.setOfferCreationAvailability();
    }
    if (changes?.offerAction && !changes?.offerAction.firstChange) {
      if (changes.offerAction.currentValue === BaseCustomerOfferActions.accept) {
        this.openAcceptOfferModal();
      }
      if (changes.offerAction.currentValue === BaseCustomerOfferActions.reject) {
        this.openRejectOfferModal();
      }
    }
  }

  toOperations(offer: CustomerInquiryOfferListItemResponse & { order?: number }): void {
    this.openOfferInfoModal.emit(offer);
  }

  openRejectOfferModal(): void {
    this.rejectOfferForm.reset();
    this.isRejectFormSubmitted = false;
    this.profileName = `${this.offer.data.profile.firstName} ${this.offer.data.profile.lastName}`;
    this.modalRef = this.modalService.open(
      {
        headText: 'inquiry.rejectOffer',
        bodyTemplate: this.rejectOfferTemplate,
        hideFooter: true,
      },
      { size: 'lg', backdrop: 'static' }
    );

    this.modalRef.result.then(() => noop, noop);
  }

  rejectOffer(): void {
    this.isRejectFormSubmitted = true;
    const body = {
      ...this.rejectOfferForm.getRawValue(),
      decliningCategory: this.translatePipe.transform(
        this.rejectOfferForm.get('decliningCategory')?.getRawValue().label
      ),
    };

    if (this.rejectOfferForm.valid) {
      this.store.dispatch(
        baseInquiryActions.declineOfferCustomer({
          payload: {
            offer: this.offer.data.id,
            inquiry: this.offer.data.inquiryId as number,
            body,
          },
        })
      );
      this.closeModal();
    }
  }

  openAcceptOfferModal(): void {
    this.modalRef = this.modalService.open(
      this.isAcceptOfferAvailable
        ? {
            headText: 'inquiry.acceptOffer',
            bodyTemplate: this.acceptOfferModal,
            okText: 'action.yesAccept',
          }
        : {
            headText: 'inquiry.acceptOfferValidationPopup.headText',
            bodyText: 'inquiry.acceptOfferValidationPopup.bodyText',
            okButtonDisplayed: false,
            cancelButtonDisplayed: false,
          },
      { size: 'lg' }
    );

    this.modalRef.result.then(() => {
      this.acceptOffer();
    }, noop);
  }

  handleOfferPreselection(): void {
    this.store.dispatch(
      baseInquiryActions.customerInquiryOfferPreselection({
        payload: {
          inquiry: this.offer.data.inquiryId as number,
          offer: this.offer.data.id,
          preselect: !this.offer.data.preselected,
        },
      })
    );
  }

  closeModal(): void {
    this.modalRef.close();
  }

  private setOfferCreationAvailability(): void {
    this.offerPositionsToAccept = this.prepareOfferPositionsToAccept();
    this.isAcceptOfferAvailable = this.offerPositionsToAccept.every((position) => position.selectedShifts.length === 1);
    this.isShiftsDeselectionMode = this.offerPositionsToAccept.length > 0;
  }

  private prepareOfferPositionsToAccept(): BaseCustomerOfferPositionsToAcceptInterface[] {
    let positions: BaseCustomerOfferPositionsToAcceptInterface[] = [];

    this.offer.data.positions.forEach((positionItem) => {
      const dateItemByPositionDate = this.offer.dates.find((dateItem) => dateItem.date === positionItem.date);
      if (dateItemByPositionDate) {
        let selectedShifts: BaseInquiryShifts[] = [];
        if (positionItem.flexShift) {
          selectedShifts.push(
            BaseInquiryShifts.FD || BaseInquiryShifts.ZD || BaseInquiryShifts.SD || BaseInquiryShifts.ND
          );
        } else {
          dateItemByPositionDate.shifts.forEach((shiftItem) => {
            if (shiftItem.shift !== BaseInquiryShiftsWithFlex.FLEX) {
              selectedShifts.push(shiftItem.shift as BaseInquiryShifts);
            }
          });
        }

        if (selectedShifts.length > 0) {
          positions.push({
            id: positionItem.id as number,
            selectedShifts,
          });
        }
      }
    });
    return positions;
  }

  private acceptOffer(): void {
    if (this.isAcceptOfferAvailable) {
      this.store.dispatch(
        baseInquiryActions.acceptOffer({
          payload: {
            inquiry: this.offer.data.inquiryId as number,
            offer: this.offer.data.id,
            offerPositions: this.offerPositionsToAccept.map((position) => {
              return { id: position.id, selectedShift: position.selectedShifts[0] };
            }),
          },
        })
      );
    }
  }

  private setStatusValues(): void {
    this.isOfferConfirmed = this.offer.data.status === BaseOfferStatus.confirmed;
    this.isOfferOpen = this.offer.data.status === BaseOfferStatus.open;
    this.isOfferDeclined = this.offer.data.status === BaseOfferStatus.declined;
    this.isOfferConfirmationNeeded = this.offer.data.status === BaseOfferStatus.needsConfirmation;
    this.isOfferUpdateNeeded = this.offer.data.status === BaseOfferStatus.needsUpdate;
  }

  private initializeActionButtonStates(): void {
    this.isAcceptButtonAvailable = this.offerActionsService.checkAcceptButtonAvailability(
      this.offer.data,
      this.locationWorkCouncilSettings
    );
    this.isRejectButtonAvailable = this.offerActionsService.checkRejectButtonAvailability(this.offer.data);
    this.isWithdrawButtonAvailable = this.offerActionsService.checkWithdrawButtonAvailability(this.offer.data);
    this.isPreselectButtonAvailable = this.offerActionsService.checkPreselectButtonAvailability(this.offer.data);
    this.isUnselectButtonAvailable = this.offerActionsService.checkUnselectButtonAvailability(
      this.offer.data,
      this.locationWorkCouncilSettings
    );
  }
}
